/**
 * @fileoverview Declarative document-edit operations ("tools") the Squirrel agent emits to
 * create/modify a DocumentLayout, plus a pure reducer that applies them (Phase AI — G-AI-SQUIRREL).
 * @module document-render/documentBuilderOps
 *
 * Responsibilities: - Define the DocumentOp union (the agent's tool vocabulary) and applyDocumentOps,
 *                     a pure reducer that applies a sequence of ops to a DocumentLayout using the
 *                     shared, validated band operations.
 * Dependencies:     - ./documentLayout (model), ./documentBuilderPalette (band ops + palette).
 * Architecture:     - Pure + framework-agnostic so the same op list can be applied on the server
 *                     (preview) and in the canvas (live). Invalid/unknown ops are ignored, so a
 *                     partially-valid agent response still produces a usable layout.
 * Notes:            - HTML carried by ops is NOT sanitized here; callers sanitize at the trust
 *                     boundary (worker render-path + sync write-path) as with all band HTML.
 */

import type { Band, BandKind, DocumentLayout, DocumentType } from './documentLayout';
import { addBand, createBand, moveBand, removeBand, paletteItemFor, canCarryAuthoredHtml } from './documentBuilderPalette';

const VALID_DOCUMENT_TYPES: ReadonlySet<DocumentType> = new Set<DocumentType>([
  'inspectionReport', 'permit', 'noticeOfViolation', 'letter', 'certificateOfCompliance', 'custom',
]);

/**
 * @contract DocumentOp
 * Purpose: One declarative edit the agent can request against a DocumentLayout.
 * Fields:  op discriminator + op-specific payload.
 * Rules:   ids reference existing bands; unknown band kinds / ids make the op a no-op.
 * Sync:    Transient — applied in-memory, persisted only when the template is saved.
 */
export type DocumentOp =
  | { op: 'setDocumentType'; documentType: string }
  | { op: 'setPage'; size?: 'letter' | 'a4' | 'legal'; orientation?: 'portrait' | 'landscape' }
  | { op: 'addBand'; kind: string; index?: number; html?: string }
  | { op: 'removeBand'; id: string }
  | { op: 'moveBand'; id: string; toIndex: number }
  | { op: 'setBandHtml'; id: string; html: string };

/** Set the html of a band's first text element. Immutable. No-op for dynamic/separator bands. */
function setBandHtml(layout: DocumentLayout, id: string, html: string): DocumentLayout {
  const apply = (band: Band): Band => {
    if (band.id !== id) return band;
    if (!canCarryAuthoredHtml(band.kind)) return band;
    const first = band.elements[0];
    const el = { id: first?.id ?? `${band.id}-el`, type: 'text' as const, html };
    return { ...band, elements: [el, ...band.elements.slice(1)] };
  };
  return {
    ...layout,
    header: layout.header ? apply(layout.header) : layout.header,
    footer: layout.footer ? apply(layout.footer) : layout.footer,
    body: layout.body.map(apply),
  };
}

/** Apply a single op (immutably); unknown/invalid ops return the layout unchanged. */
function applyOne(layout: DocumentLayout, op: DocumentOp): DocumentLayout {
  switch (op?.op) {
    case 'setDocumentType':
      return VALID_DOCUMENT_TYPES.has(op.documentType as DocumentType)
        ? { ...layout, documentType: op.documentType as DocumentType }
        : layout;
    case 'setPage':
      return {
        ...layout,
        page: {
          ...layout.page,
          ...(op.size ? { size: op.size } : {}),
          ...(op.orientation ? { orientation: op.orientation } : {}),
        },
      };
    case 'addBand': {
      if (!paletteItemFor(op.kind as BandKind)) return layout; // unknown kind
      // createBand keeps authored html for every content/structural kind (dynamic bands get a
      // binding instead), so the agent can no longer drop content for recipientBlock/legal/etc.
      return addBand(layout, createBand(op.kind as BandKind, op.html), op.index);
    }
    case 'removeBand':
      return removeBand(layout, op.id);
    case 'moveBand':
      return moveBand(layout, op.id, op.toIndex);
    case 'setBandHtml':
      return setBandHtml(layout, op.id, op.html);
    default:
      return layout;
  }
}

/**
 * @process applyDocumentOps
 * Steps:   Fold a sequence of DocumentOps over a DocumentLayout, applying each via the shared,
 *          validated band operations. Non-array input or invalid ops are ignored.
 * Inputs:  layout, ops[].
 * Outputs: new DocumentLayout (immutable).
 * Idempotency: NOT REQUIRED (ops can add/remove).
 */
export function applyDocumentOps(layout: DocumentLayout, ops: DocumentOp[] | unknown): DocumentLayout {
  if (!Array.isArray(ops)) return layout;
  return ops.reduce<DocumentLayout>((acc, op) => applyOne(acc, op as DocumentOp), layout);
}
